import { useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type { GeoBrowseGene } from '../../types';
import { useGeoBrowseContext } from '../../context/GeoBrowseContext';
import { useGeoBrowseQuery } from '../../hooks/useGeoBrowseThresholds';
import { fetchGenes } from '../../services/geoBrowseApi';
import { parseGenesParam } from '../../utils/geneSelection';
import BrowseDataTable, { type BrowseColumn } from '../BrowseDataTable';
import PageHeader from '../PageHeader';
import { InlineBar } from '../ui/GeoBrowseUi';
import styles from '../../GeoBrowse.module.css';

const MAX_SELECTED = 12;

export default function GenesView() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { thresholds, refreshKey, mergePathWithSearchParams } = useGeoBrowseContext();
  const { data, loading, error } = useGeoBrowseQuery(
    () => fetchGenes({ ...thresholds, sort: 'n_studies_sig' }),
    [thresholds.padj, thresholds.fc, refreshKey],
  );

  const selectedGenes = useMemo(() => parseGenesParam(searchParams.get('genes')), [searchParams]);
  const selectedGeneSymbols = useMemo(() => new Set(selectedGenes), [selectedGenes]);

  const toggleGene = useCallback(
    (symbol: string) => {
      const next = selectedGeneSymbols.has(symbol)
        ? selectedGenes.filter(s => s !== symbol)
        : [...selectedGenes, symbol].slice(0, MAX_SELECTED);

      const params = new URLSearchParams(searchParams);
      if (next.length) {
        params.set('genes', next.join(','));
      } else {
        params.delete('genes');
        params.delete('view');
      }
      setSearchParams(params, { replace: true });
    },
    [searchParams, selectedGenes, selectedGeneSymbols, setSearchParams],
  );

  const openGeneDetails = useCallback(() => {
    navigate(mergePathWithSearchParams('/geo-browse/gene-detail'));
  }, [mergePathWithSearchParams, navigate]);

  const maxDiseasesSig = useMemo(
    () => Math.max(1, ...(data ?? []).map(g => g.n_diseases_sig)),
    [data],
  );

  const maxStudiesSig = useMemo(
    () => Math.max(1, ...(data ?? []).map(g => g.n_studies_sig)),
    [data],
  );

  const maxSigRows = useMemo(
    () => Math.max(1, ...(data ?? []).map(g => g.n_sig_rows)),
    [data],
  );

  const columns: BrowseColumn<GeoBrowseGene>[] = useMemo(
    () => [
      {
        id: 'gene_id',
        label: 'gene_id',
        sortValue: r => r.gene_id,
        accessor: r => r.gene_id,
      },
      {
        id: 'gene_symbol',
        label: 'gene_symbol',
        sortValue: r => r.gene_symbol,
        accessor: r => r.gene_symbol,
        render: r => (
          <span className={styles.cellPrimary}>
            {r.gene_symbol}
            {selectedGeneSymbols.has(r.gene_symbol) && (
              <span className={styles.selectedGeneMark}> selected</span>
            )}
          </span>
        ),
      },
      {
        id: 'type_of_gene',
        label: 'type_of_gene',
        sortValue: r => r.type_of_gene,
        accessor: r => r.type_of_gene,
      },
      {
        id: 'n_diseases',
        label: 'n_diseases',
        sortValue: r => r.n_diseases,
        accessor: r => r.n_diseases,
      },
      {
        id: 'n_diseases_sig',
        label: 'n_diseases_sig',
        sortValue: r => r.n_diseases_sig,
        filterValue: r => r.n_diseases_sig,
        render: r => (
          <InlineBar value={r.n_diseases_sig} max={maxDiseasesSig} tone="blue" />
        ),
      },
      {
        id: 'n_studies',
        label: 'n_studies',
        sortValue: r => r.n_studies,
        accessor: r => r.n_studies,
      },
      {
        id: 'n_studies_sig',
        label: 'n_studies_sig',
        sortValue: r => r.n_studies_sig,
        filterValue: r => r.n_studies_sig,
        render: r => (
          <InlineBar value={r.n_studies_sig} max={maxStudiesSig} tone="green" />
        ),
      },
      {
        id: 'n_deg_rows',
        label: 'n_deg_rows',
        sortValue: r => r.n_deg_rows,
        accessor: r => r.n_deg_rows.toLocaleString(),
      },
      {
        id: 'n_sig_rows',
        label: 'n_sig_rows',
        sortValue: r => r.n_sig_rows,
        filterValue: r => r.n_sig_rows,
        render: r => <InlineBar value={r.n_sig_rows} max={maxSigRows} tone="green" />,
      },
    ],
    [maxDiseasesSig, maxStudiesSig, maxSigRows, selectedGeneSymbols],
  );

  if (loading) return <div className={styles.loading}>Loading genes…</div>;
  if (error) return <div className={styles.error}>{error}</div>;
  if (!data?.length) return <div className={styles.empty}>No genes found.</div>;

  return (
    <>
      <PageHeader
        title="Genes"
        description="Click rows to select/deselect genes for Gene Details. Selection is kept in the URL."
      />

      {selectedGenes.length > 0 && (
        <div className={styles.geneSelectionBar}>
          <span>
            {selectedGenes.length} gene{selectedGenes.length === 1 ? '' : 's'} selected
          </span>
          <button type="button" className={styles.geneSelectionOpenBtn} onClick={openGeneDetails}>
            Open in Gene Details
          </button>
        </div>
      )}

      <BrowseDataTable
        rows={data}
        columns={columns}
        rowKey={r => String(r.gene_id)}
        searchPlaceholder=""
        defaultPageSize={25}
        defaultSort={{ columnId: 'n_studies_sig', direction: 'desc' }}
        isRowHighlighted={row => selectedGeneSymbols.has(row.gene_symbol)}
        searchFilter={(row, q) =>
          `${row.gene_symbol} ${row.gene_id} ${row.type_of_gene}`.toLowerCase().includes(q)
        }
        onRowClick={row => toggleGene(row.gene_symbol)}
      />
    </>
  );
}
